// Supervision's stand-in for ptc-core's `useApi` (web/src/api/client.ts), for the code copied from core that calls the
// CRM API with the signed-in staff member's own token: the notification bell, the hub list and read-on-arrival (fleet
// staff notifications, spec v0.2 §8). Same `get`/`post` shape as core's client, so the copied files keep their calls.
//
// The token is for the CRM API's delegated scope, acquired silently on supervision's existing MSAL instance: the scope
// the Feedback button already uses (src/api/feedback.ts), so no new consent is involved. Paths are relative to the CRM
// API's `v1` root, as in core: `/notifications?page=1`.

import { useMemo } from 'react';
import { useMsal } from '@azure/msal-react';
import { CRM_FEEDBACK_SCOPE } from './feedback';

export const CRM_API_V1 = 'https://ptc-core-crm-api.azurewebsites.net/api/v1';
export const CRM_API_SCOPE = CRM_FEEDBACK_SCOPE;

export interface CrmApi {
  get<T>(path: string): Promise<T>;
  post<T>(path: string, body?: unknown): Promise<T>;
}

export function useApi(): CrmApi {
  const { instance, accounts } = useMsal();
  return useMemo(() => {
    async function call<T>(method: 'GET' | 'POST', path: string, body?: unknown): Promise<T> {
      const account = instance.getActiveAccount() ?? accounts[0];
      const { accessToken } = await instance.acquireTokenSilent({ scopes: [CRM_API_SCOPE], account });
      const resp = await fetch(`${CRM_API_V1}${path}`, {
        method,
        headers: {
          Authorization: `Bearer ${accessToken}`,
          ...(body === undefined ? {} : { 'Content-Type': 'application/json' }),
        },
        body: body === undefined ? undefined : JSON.stringify(body),
        // A POST (marking an item read) may be followed at once by navigating to another app: keepalive lets it finish.
        keepalive: method === 'POST',
      });
      if (!resp.ok) throw new Error(`CRM API ${method} ${path.split('?')[0]} answered ${resp.status}`);
      const text = await resp.text();
      return (text ? JSON.parse(text) : undefined) as T;
    }
    return {
      get: <T,>(path: string) => call<T>('GET', path),
      post: <T,>(path: string, body?: unknown) => call<T>('POST', path, body),
    };
  }, [instance, accounts]);
}
